import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo, useState } from "react";
import { Search } from "lucide-react";

export default function SchoolHeadStudents() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");

  const { data: students = [], isLoading } = useQuery({
    queryKey: ["sh-students-full", user?.schoolId],
    enabled: !!user?.schoolId,
    queryFn: async () => {
      const all: any[] = [];
      let from = 0;
      const size = 1000;
      while (true) {
        const { data, error } = await supabase
          .from("students")
          .select("id,surname,other_names,class,stream,sex,dormitory,medical_alerts")
          .eq("school_id", user!.schoolId)
          .eq("is_archived", false)
          .order("surname")
          .range(from, from + size - 1);
        if (error) throw error;
        all.push(...(data || []));
        if (!data || data.length < size) break;
        from += size;
      }
      return all;
    },
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) =>
      `${s.surname} ${s.other_names || ""} ${s.class || ""}`.toLowerCase().includes(q)
    );
  }, [students, query]);

  return (
    <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Students</h1>
          <p className="text-sm text-muted-foreground">
            Read-only directory. Showing alert counts only — detailed diagnoses are hidden.
          </p>
        </div>

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search name or class…"
            className="pl-10"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Class</th>
                    <th className="px-4 py-3">Stream</th>
                    <th className="px-4 py-3">Sex</th>
                    <th className="px-4 py-3">Dormitory</th>
                    <th className="px-4 py-3">Medical alerts</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr><td className="px-4 py-6 text-muted-foreground" colSpan={6}>Loading…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td className="px-4 py-6 text-muted-foreground" colSpan={6}>No students found.</td></tr>
                  ) : (
                    filtered.slice(0, 500).map((s) => {
                      const alerts = Array.isArray(s.medical_alerts) ? s.medical_alerts.length : 0;
                      return (
                        <tr key={s.id} className="border-t">
                          <td className="px-4 py-2 font-medium">
                            {s.surname} {s.other_names || ""}
                          </td>
                          <td className="px-4 py-2">{s.class || "—"}</td>
                          <td className="px-4 py-2">{s.stream || "—"}</td>
                          <td className="px-4 py-2">{s.sex || "—"}</td>
                          <td className="px-4 py-2">{s.dormitory || "—"}</td>
                          <td className="px-4 py-2">
                            {alerts > 0 ? (
                              <Badge variant="destructive">{alerts}</Badge>
                            ) : (
                              <span className="text-muted-foreground">None</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              {filtered.length > 500 && (
                <div className="border-t px-4 py-2 text-xs text-muted-foreground">
                  Showing first 500 of {filtered.length} — refine your search to see more.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
  );
}
